/**
 * Permission handling utilities for microphone access
 * Handles getUserMedia permission requests and state management
 */

import i18next from 'i18next';
import type { AudioConstraints, AudioRecorderError, AudioRecorderErrorType } from '../models/audio-recorder.types';
import { PermissionState } from '../models/audio-recorder.types';
import { getAudioConstraints, isIOS, isSafari } from './audio-recorder-browser-compat';

/**
 * Request microphone permission from user
 * @returns MediaStream if granted, throws error if denied
 */
export const requestMicrophonePermission = async (constraints?: AudioConstraints): Promise<MediaStream> => {
  try {
    const audioConstraints = constraints || getAudioConstraints();
    const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
    return stream;
  } catch (error) {
    throw createPermissionError(error as Error);
  }
};

/**
 * Check current microphone permission status
 * @returns Current permission state
 */
export const checkPermissionStatus = async (): Promise<PermissionState> => {
  // Safari/iOS don't support Permissions API
  if (isSafari() || isIOS()) {
    return PermissionState.UNKNOWN;
  }

  try {
    if (navigator.permissions?.query) {
      const result = await navigator.permissions.query({
        name: 'microphone' as PermissionName
      });
      return result.state as PermissionState;
    }
  } catch (error) {
    console.warn('Permissions API query failed:', error);
  }

  return PermissionState.UNKNOWN;
};

/**
 * Test microphone access without keeping the stream
 * @returns true if permission granted, false otherwise
 */
export const testMicrophoneAccess = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Immediately stop all tracks
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    const err = error as Error;
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      return false;
    }
    // Other errors (NotFoundError, etc.) also count as no access
    return false;
  }
};

/**
 * Create user-friendly error from permission failure
 */
export const createPermissionError = (error: Error): AudioRecorderError => {
  const errorName = error.name;

  switch (errorName) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return {
        type: 'PERMISSION_DENIED' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.permissionDenied'),
        originalError: error,
        recoverable: true
      };

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        type: 'DEVICE_NOT_FOUND' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.deviceNotFound'),
        originalError: error,
        recoverable: false
      };

    case 'NotReadableError':
    case 'TrackStartError':
      return {
        type: 'RECORDING_FAILED' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.recordingFailed'),
        originalError: error,
        recoverable: true
      };

    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return {
        type: 'NOT_SUPPORTED' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.notSupported'),
        originalError: error,
        recoverable: false
      };

    case 'TypeError':
      return {
        type: 'NOT_SUPPORTED' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.browserNotSupported'),
        originalError: error,
        recoverable: false
      };

    case 'SecurityError':
      return {
        type: 'PERMISSION_DENIED' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.securityError'),
        originalError: error,
        recoverable: false
      };

    default:
      return {
        type: 'UNKNOWN_ERROR' as AudioRecorderErrorType,
        message: i18next.t('messages.recorder.errors.unknownError', { message: error.message }),
        originalError: error,
        recoverable: false
      };
  }
};

/**
 * Monitor permission changes (if supported by browser)
 */
export const monitorPermissionChanges = (callback: (state: PermissionState) => void): (() => void) | null => {
  // Safari/iOS don't support permission monitoring
  if (isSafari() || isIOS()) {
    return null;
  }

  let permissionStatus: PermissionStatus | null = null;

  const handleChange = () => {
    if (permissionStatus) {
      callback(permissionStatus.state as PermissionState);
    }
  };

  // Set up monitoring
  navigator.permissions
    ?.query({ name: 'microphone' as PermissionName })
    .then((status) => {
      permissionStatus = status;
      status.addEventListener('change', handleChange);
    })
    .catch((error) => {
      console.warn('Could not monitor permission changes:', error);
    });

  // Return cleanup function
  return () => {
    if (permissionStatus) {
      permissionStatus.removeEventListener('change', handleChange);
    }
  };
};
